import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, Loader2, MailWarning } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { safeNextPath } from "@/utils/authUtils";
import {
  AUTH_PRIMARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";

type State = "verifying" | "success" | "expired";
type EmailOtpType = "signup" | "recovery" | "magiclink" | "email_change" | "invite";

/**
 * /auth/confirm — callback landing page after Supabase Auth verifies a token
 * (signup confirmation, password recovery, magic link, or email change).
 *
 * Handles both flow types defensively:
 * - Implicit flow (default for supabase-js v2): session is delivered in URL hash
 *   fragment, the Supabase client auto-exchanges via detectSessionInUrl. We poll
 *   getSession() up to 2s for the session to appear.
 * - PKCE flow: link arrives with ?token_hash=&type=. We call verifyOtp manually.
 *
 * Three terminal states:
 * - verifying — brief spinner while exchange is in progress
 * - success   — redirect to `next` query param (default /)
 * - expired   — friendly error + "Send new link" button that calls auth.resend
 */
export default function AuthConfirm() {
  const [state, setState] = useState<State>("verifying");
  const [resending, setResending] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // `next` arrives from the emailed link, so it is untrusted — see safeNextPath.
  const next = safeNextPath(searchParams.get("next"), "/");
  const email = searchParams.get("email") || "";

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // PKCE branch: token_hash + type in query string → call verifyOtp.
      const tokenHash = searchParams.get("token_hash");
      const type = searchParams.get("type") as EmailOtpType | null;

      if (tokenHash && type) {
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
        if (cancelled) return;
        if (error) {
          setState("expired");
          return;
        }
      }

      // Implicit flow OR post-verifyOtp: poll for active session up to 2s.
      // detectSessionInUrl handles hash fragments on Supabase client init, so
      // the session should appear quickly.
      const deadline = Date.now() + 2000;
      while (Date.now() < deadline) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session) {
          setState("success");
          setTimeout(() => {
            if (!cancelled) navigate(next, { replace: true });
          }, 500);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (!cancelled) setState("expired");
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigate, next, searchParams]);

  const handleResend = async () => {
    if (!email) {
      toast.error("No email on file for resend. Please sign in and request a new link.");
      return;
    }
    setResending(true);
    try {
      const redirectUrl = `${window.location.origin}/auth/confirm?next=${encodeURIComponent(next)}`;
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: redirectUrl },
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success("New verification link sent. Check your email.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthPageLayout>
      {state === "verifying" && (
        <AuthPageHeader
          title="Verifying…"
          description="Confirming your email with Franklin."
          icon={<Loader2 className="h-6 w-6 animate-spin" />}
          iconClassName="bg-blue-100 text-blue-600"
        />
      )}

      {state === "success" && (
        <AuthPageHeader
          title="Email verified"
          description="Redirecting…"
          icon={<CheckCircle className="h-6 w-6" />}
          iconClassName="bg-green-100 text-green-600"
        />
      )}

      {state === "expired" && (
        <>
          <AuthPageHeader
            title="This link expired"
            description="Verification links are valid for 24 hours."
            icon={<MailWarning className="h-6 w-6" />}
            iconClassName="bg-amber-100 text-amber-600"
          />
          <div className="space-y-4">
            <Alert className="rounded-xl">
              <AlertDescription>Request a fresh verification link below.</AlertDescription>
            </Alert>
            <Button
              onClick={handleResend}
              disabled={resending || !email}
              className={AUTH_PRIMARY_ACTION_CLASSES}
            >
              {resending ? "Sending…" : "Send new link"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              className="h-11 w-full rounded-xl text-[14px] font-semibold"
            >
              Back to login
            </Button>
          </div>
        </>
      )}
    </AuthPageLayout>
  );
}
